import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileCheck, Send } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker, Field, inputCls } from "@/components/form";
import { api, formatApiError } from "@/lib/api";

const CONFIG = {
  "versicherung-privat": { area: "versicherung", type: "angebot", title: "Angebot – Privatversicherung",
    intro: "Fordern Sie ein unverbindliches Angebot für Ihre private Absicherung an.",
    fields: [{ n: "sparte", l: "Sparte", t: "select", o: ["KFZ-Versicherung", "Privathaftpflicht", "Hausrat", "Wohngebäude", "Berufsunfähigkeit", "Krankenversicherung", "Altersvorsorge"] }] },
  "versicherung-gewerbe": { area: "versicherung", type: "angebot", title: "Angebot – Gewerbeversicherung",
    intro: "Maßgeschneiderter Schutz für Ihr Unternehmen – jetzt Angebot anfordern.",
    fields: [{ n: "sparte", l: "Bereich", t: "select", o: ["Betriebshaftpflicht", "Inhaltsversicherung", "D&O", "Cyber-Versicherung", "Berufshaftpflicht"] }, { n: "company", l: "Unternehmen", t: "text" }] },
  "versicherung-vergleich": { area: "versicherung", type: "vergleich", title: "Versicherungsvergleich anfordern",
    intro: "Wir vergleichen unabhängig hunderte Tarife und finden Ihren besten Schutz zum besten Preis.",
    fields: [{ n: "sparte", l: "Welche Versicherung?", t: "select", o: ["KFZ-Versicherung", "Privathaftpflicht", "Hausrat", "Wohngebäude", "Berufsunfähigkeit", "Krankenversicherung", "Gewerbeversicherung"] }, { n: "current_insurer", l: "Aktueller Anbieter (optional)", t: "text" }] },
  "finanzierung-bau": { area: "finanzierung", type: "antrag", title: "Baufinanzierung anfragen",
    intro: "Sichern Sie sich Top-Konditionen für Ihre Immobilienfinanzierung.",
    fields: [{ n: "amount", l: "Finanzierungssumme (€)", t: "number" }, { n: "purpose", l: "Vorhaben", t: "select", o: ["Kauf", "Neubau", "Modernisierung", "Anschlussfinanzierung"] }] },
  "finanzierung-privat": { area: "finanzierung", type: "antrag", title: "Privatkredit anfragen",
    intro: "Flexible Ratenkredite zu fairen Konditionen.",
    fields: [{ n: "amount", l: "Kreditsumme (€)", t: "number" }, { n: "purpose", l: "Verwendungszweck", t: "text" }] },
  "finanzierung-gewerbe": { area: "finanzierung", type: "antrag", title: "Gewerbefinanzierung anfragen",
    intro: "Kapital für Investitionen und Wachstum Ihres Unternehmens.",
    fields: [{ n: "amount", l: "Finanzierungssumme (€)", t: "number" }, { n: "company", l: "Unternehmen", t: "text" }] },
};

export default function Anfrage() {
  const { slug } = useParams();
  const cfg = CONFIG[slug];
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", gdpr_consent: false });
  const [extra, setExtra] = useState({});
  const [loading, setLoading] = useState(false);
  if (!cfg) return <Navigate to="/" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setEx = (k) => (e) => setExtra({ ...extra, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, business_area: cfg.area, source: slug, request_type: cfg.type,
        topic: cfg.title, details: extra, message: `${cfg.title}\n${Object.entries(extra).map(([k, v]) => `${k}: ${v}`).join("\n")}\n${form.message}` });
      toast.success("Ihre Anfrage wurde übermittelt. Wir melden uns zeitnah!");
      setForm({ name: "", email: "", phone: "", message: "", gdpr_consent: false }); setExtra({});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid={`anfrage-${slug}`} className="bg-navy-900 min-h-screen">
      <Seo title={cfg.title} description={cfg.intro} path={`/anfrage/${slug}`} />
      <section className="pt-40 pb-24">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Kicker>Anfrage</Kicker>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-none flex items-center gap-4"><FileCheck className="text-gold-500 hidden sm:block" size={42} strokeWidth={1.4} /> {cfg.title}</h1>
            <p className="mt-5 text-slate-300 leading-relaxed">{cfg.intro}</p>
          </motion.div>
          <motion.form data-testid="anfrage-form" onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-8 bg-navy-800 border border-white/5 rounded-md p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name *"><input data-testid="anfrage-name" required className={inputCls} value={form.name} onChange={set("name")} /></Field>
              <Field label="Telefon"><input data-testid="anfrage-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
            </div>
            <Field label="E-Mail *"><input data-testid="anfrage-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
            {cfg.fields.map((f) => (
              <Field key={f.n} label={f.l}>
                {f.t === "select"
                  ? <select data-testid={`anfrage-${f.n}`} className={inputCls} value={extra[f.n] || ""} onChange={setEx(f.n)}><option value="" className="bg-navy-800">Bitte wählen</option>{f.o.map((o) => <option key={o} value={o} className="bg-navy-800">{o}</option>)}</select>
                  : <input data-testid={`anfrage-${f.n}`} type={f.t} className={inputCls} value={extra[f.n] || ""} onChange={setEx(f.n)} />}
              </Field>
            ))}
            <Field label="Nachricht"><textarea data-testid="anfrage-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} /></Field>
            <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
              <input data-testid="anfrage-gdpr" type="checkbox" checked={form.gdpr_consent} onChange={set("gdpr_consent")} className="mt-0.5 accent-gold-500 w-4 h-4" />
              <span>Ich stimme der Verarbeitung meiner Daten gemäß Datenschutzerklärung zu.</span>
            </label>
            <button data-testid="anfrage-submit" disabled={loading} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-4 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors disabled:opacity-60">{loading ? "Sendet…" : <>Anfrage absenden <Send size={16} /></>}</button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
